/**
 * Size Guide Drawer — generic engine.
 *
 * Reads canonical chart_data shapes (see tools/sizeguides/{matrix,ranges}.schema.json):
 *   - {type:"matrix", x_axis, y_axis, grid}
 *   - {type:"ranges", measurements[], sizes[], size_systems?}
 *
 * Renders the inputs ("Find Your Size") and the chart table dynamically from
 * the data — no hardcoded measurement assumptions. Recommendation dispatches
 * on `type`. Cross-product input values persist in localStorage keyed by
 * measurement key (height_cm, weight_kg, chest_cm, ...).
 */
(function () {
  var STORAGE_KEY = '2xu_size_finder';
  var SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

  // Translatable strings injected by size-guide-drawer.liquid (#SizeGuideStrings).
  // Falls back to the prior hardcoded Swedish copy so nothing breaks if the tag
  // is absent or malformed.
  function loadStrings() {
    var defaults = {
      intro: 'Ange dina mått för en storleksrekommendation.',
      result_single: 'Baserat på dina mått rekommenderar vi storlek {{ size }}.',
      result_between: 'Du är mellan storlekar. Välj {{ smaller }} för en tightare passform eller {{ larger }} för en mer avslappnad passform.'
    };
    var el = document.getElementById('SizeGuideStrings');
    if (!el) return defaults;
    try {
      var parsed = JSON.parse(el.textContent);
      if (parsed && typeof parsed === 'object') {
        Object.keys(defaults).forEach(function (k) {
          if (parsed[k] != null && parsed[k] !== '') defaults[k] = parsed[k];
        });
      }
    } catch (e) { /* keep defaults */ }
    return defaults;
  }
  var STRINGS = loadStrings();

  // Measurement labels for known keys. Localized values come from the injected
  // strings blob (#SizeGuideStrings → measurements), which Liquid resolves to
  // the active Shopify market language. The Swedish map below is a fallback used
  // only when the blob is absent or missing a key; beyond that we fall back to
  // chart_data's own `label`, then the raw key.
  var FALLBACK_LABELS = {
    height_cm: 'Längd',
    weight_kg: 'Vikt',
    chest_cm: 'Bröst',
    bust_cm: 'Byst',
    underbust_cm: 'Under byst',
    overbust_cm: 'Över byst',
    waist_cm: 'Midja',
    hip_cm: 'Höft',
    inseam_cm: 'Innerben',
    bicep_cm: 'Biceps',
    forearm_cm: 'Underarm',
    quadricep_cm: 'Lår',
    calf_cm: 'Vad',
    calf_girth_cm: 'Vadens omkrets',
    thigh_cm: 'Lårets omkrets',
    shoe_size_eu: 'Skostorlek',
    calf_size_cm: 'Vadens storlek',
    palm_circumference_cm: 'Handflatans omkrets',
    hand_length_cm: 'Handens längd'
  };
  function loadMeasurementLabels() {
    var labels = {};
    Object.keys(FALLBACK_LABELS).forEach(function (k) { labels[k] = FALLBACK_LABELS[k]; });
    var el = document.getElementById('SizeGuideStrings');
    if (el) {
      try {
        var parsed = JSON.parse(el.textContent);
        if (parsed && parsed.measurements && typeof parsed.measurements === 'object') {
          Object.keys(parsed.measurements).forEach(function (k) {
            if (parsed.measurements[k] != null && parsed.measurements[k] !== '') {
              labels[k] = parsed.measurements[k];
            }
          });
        }
      } catch (e) { /* keep fallbacks */ }
    }
    return labels;
  }
  var MEASUREMENT_LABELS = loadMeasurementLabels();

  // ---------------------------------------------------------------------------
  // Generic helpers
  // ---------------------------------------------------------------------------
  function findClosestIndex(value, arr) {
    var closest = 0;
    var minDist = Math.abs(value - arr[0]);
    for (var i = 1; i < arr.length; i++) {
      var dist = Math.abs(value - arr[i]);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    }
    return closest;
  }

  function inputLabel(m) {
    return MEASUREMENT_LABELS[m.key] || m.label || m.key;
  }

  function unitSuffix(m) {
    return m.unit ? ' (' + m.unit + ')' : '';
  }

  function compareKnownSize(a, b) {
    var ai = SIZE_ORDER.indexOf(String(a).toUpperCase());
    var bi = SIZE_ORDER.indexOf(String(b).toUpperCase());
    if (ai === -1 || bi === -1) return 0;
    return ai - bi;
  }

  // Returns saved measurement values as a flat dict keyed by measurement key.
  function loadSavedValues() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  function saveValues(values) {
    try {
      var existing = loadSavedValues();
      var merged = {};
      Object.keys(existing).forEach(function (k) { merged[k] = existing[k]; });
      Object.keys(values).forEach(function (k) {
        if (values[k] !== '' && values[k] != null) merged[k] = values[k];
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    } catch (e) { /* silent */ }
  }

  // ---------------------------------------------------------------------------
  // Data load
  // ---------------------------------------------------------------------------
  function loadChartData() {
    var dataEl = document.getElementById('SizeGuideData');
    if (!dataEl) return null;
    try {
      var raw = JSON.parse(dataEl.textContent);
      var parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!parsed || (parsed.type !== 'matrix' && parsed.type !== 'ranges')) {
        console.warn('Size guide: chart_data is missing a recognized `type` field', parsed);
        return null;
      }
      return parsed;
    } catch (e) {
      console.warn('Size guide: could not parse chart_data', e);
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Input rendering — measurements drive the input set
  // ---------------------------------------------------------------------------
  function inputsForChart(chart) {
    if (chart.type === 'matrix') {
      return [
        { key: chart.y_axis.key, label: chart.y_axis.label, unit: chart.y_axis.unit, values: chart.y_axis.values },
        { key: chart.x_axis.key, label: chart.x_axis.label, unit: chart.x_axis.unit, values: chart.x_axis.values }
      ];
    }
    // ranges
    return chart.measurements.map(function (m) {
      return { key: m.key, label: m.label, unit: m.unit };
    });
  }

  function renderInputs(container, inputs, saved) {
    container.innerHTML = '';
    inputs.forEach(function (m) {
      var fieldId = 'sg-input-' + m.key;
      var wrap = document.createElement('div');
      wrap.className = 'sg-drawer__field';

      var label = document.createElement('label');
      label.className = 'sg-drawer__label';
      label.setAttribute('for', fieldId);
      label.textContent = inputLabel(m) + unitSuffix(m);

      var input = document.createElement('input');
      input.type = 'number';
      input.id = fieldId;
      input.className = 'sg-drawer__input';
      input.dataset.measurementKey = m.key;
      input.step = 'any';
      input.inputMode = 'decimal';
      if (m.values && m.values.length) {
        input.min = String(m.values[0]);
        input.max = String(m.values[m.values.length - 1]);
        input.placeholder = 'ex. ' + m.values[Math.floor(m.values.length / 2)];
      } else {
        input.placeholder = '—';
      }
      if (saved[m.key] != null && saved[m.key] !== '') {
        input.value = saved[m.key];
      }

      wrap.appendChild(label);
      wrap.appendChild(input);
      container.appendChild(wrap);
    });
  }

  function readInputs(container) {
    var values = {};
    container.querySelectorAll('input[data-measurement-key]').forEach(function (el) {
      var v = parseFloat(el.value);
      if (!isNaN(v)) values[el.dataset.measurementKey] = v;
    });
    return values;
  }

  // ---------------------------------------------------------------------------
  // Table rendering
  // ---------------------------------------------------------------------------
  function renderMatrixTable(table, chart) {
    var thead = table.querySelector('thead tr');
    var tbody = table.querySelector('tbody');
    thead.innerHTML = '';
    tbody.innerHTML = '';

    // Corner cell shows the axis units stacked.
    var corner = document.createElement('th');
    corner.className = 'sg-drawer__th sg-drawer__th--corner';
    var spanY = document.createElement('span');
    spanY.className = 'sg-drawer__corner-height';
    spanY.textContent = chart.y_axis.unit || '';
    var spanX = document.createElement('span');
    spanX.className = 'sg-drawer__corner-weight';
    spanX.textContent = chart.x_axis.unit || '';
    corner.appendChild(spanY);
    corner.appendChild(spanX);
    thead.appendChild(corner);

    chart.x_axis.values.forEach(function (v) {
      var th = document.createElement('th');
      th.textContent = v;
      thead.appendChild(th);
    });

    chart.y_axis.values.forEach(function (v, rowIdx) {
      var tr = document.createElement('tr');
      var rowTh = document.createElement('th');
      rowTh.textContent = v;
      tr.appendChild(rowTh);
      chart.x_axis.values.forEach(function (_x, colIdx) {
        var td = document.createElement('td');
        var cell = chart.grid[rowIdx] ? chart.grid[rowIdx][colIdx] : null;
        td.textContent = cell || '';
        td.dataset.row = rowIdx;
        td.dataset.col = colIdx;
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
  }

  function renderRangesTable(table, chart) {
    var thead = table.querySelector('thead tr');
    var tbody = table.querySelector('tbody');
    thead.innerHTML = '';
    tbody.innerHTML = '';
    table.classList.add('sg-drawer__table--ranges');

    var corner = document.createElement('th');
    corner.className = 'sg-drawer__th sg-drawer__th--corner';
    thead.appendChild(corner);

    chart.sizes.forEach(function (s) {
      var th = document.createElement('th');
      th.textContent = s.size;
      if (s.label) th.title = s.label;
      thead.appendChild(th);
    });

    // Optional: extra header rows showing each declared size_system's aliases.
    if (chart.size_systems && chart.size_systems.length) {
      chart.size_systems.forEach(function (system) {
        var tr = document.createElement('tr');
        var th = document.createElement('th');
        th.className = 'sg-drawer__row-label--system';
        th.textContent = system.label;
        tr.appendChild(th);
        chart.sizes.forEach(function (s) {
          var cell = document.createElement('th');
          cell.className = 'sg-drawer__td--alias';
          cell.textContent = (s.aliases && s.aliases[system.key]) || s.size;
          tr.appendChild(cell);
        });
        table.querySelector('thead').appendChild(tr);
      });
    }

    chart.measurements.forEach(function (m) {
      var tr = document.createElement('tr');
      var rowTh = document.createElement('th');
      rowTh.className = 'sg-drawer__row-label--measurement';
      rowTh.textContent = inputLabel(m) + unitSuffix(m);
      tr.appendChild(rowTh);

      chart.sizes.forEach(function (s, colIdx) {
        var td = document.createElement('td');
        td.className = 'sg-drawer__td--range';
        var range = s.ranges && s.ranges[m.key];
        if (range) {
          td.textContent = range[0] === range[1]
            ? String(range[0])
            : range[0] + '–' + range[1];
        }
        td.dataset.col = colIdx;
        td.dataset.measurement = m.key;
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
  }

  // ---------------------------------------------------------------------------
  // Recommendation engine
  // ---------------------------------------------------------------------------
  function recommendMatrix(chart, values) {
    var xVal = values[chart.x_axis.key];
    var yVal = values[chart.y_axis.key];
    if (xVal == null || yVal == null) return null;

    var xs = chart.x_axis.values;
    var ys = chart.y_axis.values;
    var grid = chart.grid;

    var rowIdx = findClosestIndex(yVal, ys);
    var colIdx = findClosestIndex(xVal, xs);
    var size = grid[rowIdx] ? grid[rowIdx][colIdx] : null;

    // Snapped cell empty -> expanding-radius search for nearest non-empty.
    if (!size) {
      for (var radius = 1; radius <= 3 && !size; radius++) {
        for (var dr = -radius; dr <= radius && !size; dr++) {
          for (var dc = -radius; dc <= radius && !size; dc++) {
            var r = rowIdx + dr, c = colIdx + dc;
            if (r >= 0 && r < ys.length && c >= 0 && c < xs.length) {
              var cand = grid[r] ? grid[r][c] : null;
              if (cand) {
                size = cand;
                rowIdx = r;
                colIdx = c;
              }
            }
          }
        }
      }
    }
    if (!size) return null;

    // Lean direction: between gridlines, surface the cell the user is leaning toward.
    var leanRow = 0;
    if (yVal > ys[rowIdx] && rowIdx + 1 < ys.length) leanRow = 1;
    else if (yVal < ys[rowIdx] && rowIdx - 1 >= 0) leanRow = -1;
    var leanCol = 0;
    if (xVal > xs[colIdx] && colIdx + 1 < xs.length) leanCol = 1;
    else if (xVal < xs[colIdx] && colIdx - 1 >= 0) leanCol = -1;

    var altSize = null;
    if (leanRow !== 0 || leanCol !== 0) {
      var lr = rowIdx + leanRow, lc = colIdx + leanCol;
      var leanSize = grid[lr] ? grid[lr][lc] : null;
      if (leanSize && leanSize !== size) altSize = leanSize;
    }

    return { size: size, altSize: altSize, rowIdx: rowIdx, colIdx: colIdx };
  }

  function recommendRanges(chart, values) {
    // Score every size, even when the user is outside a range. Score is the
    // sum over declared measurements of |v - midpoint| / span — inside-range
    // yields 0–0.5 per measurement, outside continues smoothly (each extra
    // span adds 1.0). Empty state only fires when the best size has a single
    // measurement more than one full span off-chart — a genuine "we don't
    // make your size" signal, not a 1cm overshoot on one axis.
    var matches = [];
    chart.sizes.forEach(function (s, idx) {
      var totalScore = 0;
      var measured = 0;
      var worstOvershoot = 0;
      chart.measurements.forEach(function (m) {
        var v = values[m.key];
        var range = s.ranges && s.ranges[m.key];
        if (v == null || !range) return;
        measured++;
        var span = (range[1] - range[0]) || 1;
        var mid = (range[0] + range[1]) / 2;
        totalScore += Math.abs(v - mid) / span;
        var overshoot = 0;
        if (v < range[0]) overshoot = (range[0] - v) / span;
        else if (v > range[1]) overshoot = (v - range[1]) / span;
        if (overshoot > worstOvershoot) worstOvershoot = overshoot;
      });
      if (!measured) return;
      matches.push({ size: s.size, idx: idx, score: totalScore, worstOvershoot: worstOvershoot });
    });
    if (!matches.length) return null;
    matches.sort(function (a, b) { return a.score - b.score; });

    var primary = matches[0];
    if (primary.worstOvershoot > 1.0) return null;

    // Surface a second size when fit is comparable. Threshold = 0.35 per
    // measurement (matches the prior engine's intuition). Ranges overlap by
    // design — anything looser drowns the user.
    var threshold = 0.35 * chart.measurements.length;
    var alt = null;
    for (var i = 1; i < matches.length; i++) {
      if (matches[i].score - primary.score < threshold && matches[i].size !== primary.size) {
        alt = matches[i];
        break;
      }
    }
    return {
      size: primary.size,
      altSize: alt ? alt.size : null,
      colIdx: primary.idx,
      altColIdx: alt ? alt.idx : -1,
      rowIdx: -1
    };
  }

  // ---------------------------------------------------------------------------
  // Highlight (crosshair)
  // ---------------------------------------------------------------------------
  function clearHighlights(table) {
    if (!table) return;
    table.querySelectorAll('.sg-highlight, .sg-highlight-row, .sg-highlight-col')
      .forEach(function (el) {
        el.classList.remove('sg-highlight', 'sg-highlight-row', 'sg-highlight-col');
      });
  }

  function highlightMatrix(table, rowIdx, colIdx) {
    clearHighlights(table);
    if (!table) return;
    var rows = table.querySelectorAll('tbody tr');
    var headerCells = table.querySelectorAll('thead th');
    if (rows[rowIdx]) {
      rows[rowIdx].querySelectorAll('th, td').forEach(function (c) {
        c.classList.add('sg-highlight-row');
      });
    }
    if (headerCells[colIdx + 1]) headerCells[colIdx + 1].classList.add('sg-highlight-col');
    rows.forEach(function (row) {
      var cells = row.querySelectorAll('td');
      if (cells[colIdx]) cells[colIdx].classList.add('sg-highlight-col');
    });
    var cell = table.querySelector('td[data-row="' + rowIdx + '"][data-col="' + colIdx + '"]');
    if (cell) {
      cell.classList.add('sg-highlight');
      cell.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  function highlightRanges(table, colIdx) {
    clearHighlights(table);
    if (!table) return;
    var headerRows = table.querySelectorAll('thead tr');
    // Highlight the primary size cell across every header row + every body row.
    headerRows.forEach(function (hr) {
      var cells = hr.children;
      if (cells[colIdx + 1]) cells[colIdx + 1].classList.add('sg-highlight-col');
    });
    table.querySelectorAll('tbody tr').forEach(function (row) {
      var cells = row.querySelectorAll('td');
      if (cells[colIdx]) cells[colIdx].classList.add('sg-highlight-col');
    });
    var firstBodyRow = table.querySelector('tbody tr');
    if (firstBodyRow) {
      var firstHit = firstBodyRow.querySelectorAll('td')[colIdx];
      if (firstHit) firstHit.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  // ---------------------------------------------------------------------------
  // Boot — wire everything together
  // ---------------------------------------------------------------------------
  var drawer = document.getElementById('SizeGuideDrawer');
  if (!drawer) return;

  var table = document.getElementById('SizeGuideTable');
  var inputsContainer = document.getElementById('SizeGuideInputs');
  var finderSection = drawer.querySelector('.sg-drawer__finder');
  var finderIntro = drawer.querySelector('.sg-drawer__finder-intro');
  var closeButtons = drawer.querySelectorAll('[data-sg-close]');

  var resultEl = document.getElementById('SizeGuideResult');
  var resultSize = document.getElementById('SizeGuideResultSize');
  var resultText = document.getElementById('SizeGuideResultText');
  var selectBtn = document.getElementById('SizeGuideSelectBtn');
  var selectBtnSize = document.getElementById('SizeGuideSelectBtnSize');
  var selectBtnAlt = document.getElementById('SizeGuideSelectBtnAlt');
  var selectBtnAltSize = document.getElementById('SizeGuideSelectBtnAltSize');
  var resultEmpty = document.getElementById('SizeGuideResultEmpty');

  var chart = loadChartData();
  if (!chart) return;

  // Render table once on init.
  if (chart.type === 'matrix') renderMatrixTable(table, chart);
  else renderRangesTable(table, chart);

  // Inputs + recommender are conditional on chart.show_recommender (defaults true).
  var showRecommender = chart.show_recommender !== false;
  if (!showRecommender && finderSection) {
    finderSection.hidden = true;
  } else if (inputsContainer) {
    var inputs = inputsForChart(chart);
    renderInputs(inputsContainer, inputs, loadSavedValues());
    if (finderIntro) {
      finderIntro.textContent = STRINGS.intro;
    }
  }

  // -------------------------------------------------------------------------
  // Drawer open/close
  // -------------------------------------------------------------------------
  function openDrawer() {
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('sg-drawer-open');
    document.body.classList.add('sg-drawer-open');
    var closeBtn = drawer.querySelector('.sg-drawer__close');
    if (closeBtn) closeBtn.focus();
  }
  function closeDrawer() {
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('sg-drawer-open');
    document.body.classList.remove('sg-drawer-open');
    var trigger = document.querySelector('.pdp-2xu__size-guide-link');
    if (trigger) trigger.focus();
  }
  closeButtons.forEach(function (btn) { btn.addEventListener('click', closeDrawer); });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') closeDrawer();
  });
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('.pdp-2xu__size-guide-link');
    if (trigger) { e.preventDefault(); openDrawer(); }
  });

  // -------------------------------------------------------------------------
  // PDP variant selection
  // -------------------------------------------------------------------------
  function selectSizeOnPDP(size) {
    if (!size) return;
    var inputs = document.querySelectorAll('.product-form__input--pill input[type="radio"]');
    var matched = false;
    inputs.forEach(function (input) {
      if (matched) return;
      if (input.value.toUpperCase() === String(size).toUpperCase()) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        matched = true;
      }
    });
    if (matched) closeDrawer();
  }
  if (selectBtn) selectBtn.addEventListener('click', function () { selectSizeOnPDP(selectBtnSize.textContent); });
  if (selectBtnAlt) selectBtnAlt.addEventListener('click', function () { selectSizeOnPDP(selectBtnAltSize.textContent); });

  // -------------------------------------------------------------------------
  // Recommendation update
  // -------------------------------------------------------------------------
  function showResult() {
    resultEl.classList.add('sg-drawer__result--visible');
    resultEmpty.classList.remove('sg-drawer__result-empty--visible');
  }
  function showEmpty() {
    resultEmpty.classList.add('sg-drawer__result-empty--visible');
    resultEl.classList.remove('sg-drawer__result--visible');
  }
  function hideResults() {
    resultEl.classList.remove('sg-drawer__result--visible');
    resultEmpty.classList.remove('sg-drawer__result-empty--visible');
  }

  // For "between sizes" copy, decide which of two sizes is smaller. Prefer the
  // chart's declared order (ranges mode), fall back to SIZE_ORDER (matrix mode).
  function smallerLarger(primary, alt) {
    if (chart.type === 'ranges') {
      var indexOf = {};
      chart.sizes.forEach(function (s, i) { indexOf[s.size] = i; });
      if (indexOf[primary] != null && indexOf[alt] != null) {
        return indexOf[primary] < indexOf[alt] ? [primary, alt] : [alt, primary];
      }
    }
    return compareKnownSize(primary, alt) > 0 ? [alt, primary] : [primary, alt];
  }

  function updateRecommendation() {
    if (!showRecommender) return;
    var values = readInputs(inputsContainer);
    var expectedInputs = inputsForChart(chart);
    var allFilled = expectedInputs.every(function (m) { return values[m.key] != null; });
    if (!allFilled) {
      hideResults();
      clearHighlights(table);
      return;
    }

    var result = chart.type === 'matrix' ? recommendMatrix(chart, values) : recommendRanges(chart, values);
    if (!result || !result.size) {
      showEmpty();
      clearHighlights(table);
      return;
    }

    if (result.altSize) {
      var pair = smallerLarger(result.size, result.altSize);
      var smaller = pair[0], larger = pair[1];
      resultEl.classList.add('sg-drawer__result--between');
      resultSize.textContent = smaller + ' – ' + larger;
      resultText.textContent = STRINGS.result_between
        .replace('{{ smaller }}', smaller)
        .replace('{{ larger }}', larger);
      selectBtnSize.textContent = smaller;
      selectBtnAltSize.textContent = larger;
      if (selectBtnAlt) selectBtnAlt.hidden = false;
    } else {
      resultEl.classList.remove('sg-drawer__result--between');
      resultSize.textContent = result.size;
      resultText.textContent = STRINGS.result_single.replace('{{ size }}', result.size);
      selectBtnSize.textContent = result.size;
      if (selectBtnAlt) selectBtnAlt.hidden = true;
    }
    showResult();

    if (chart.type === 'matrix') highlightMatrix(table, result.rowIdx, result.colIdx);
    else highlightRanges(table, result.colIdx);

    saveValues(values);
  }

  if (inputsContainer) {
    inputsContainer.addEventListener('input', function (e) {
      if (e.target && e.target.matches('input[data-measurement-key]')) updateRecommendation();
    });
  }

  // Trigger an initial recommendation if all inputs were pre-filled from storage.
  updateRecommendation();
})();
